import { useState, useEffect, useCallback } from 'react';
import {
  extractTokenFromUrl,
  validateToken,
  storeToken,
  getStoredToken,
  clearStoredToken,
  isTokenExpired,
  getTokenTimeToExpiry,
} from '@/utils/auth';
import { verifyToken } from '@/services/api';
import type { AuthState } from '@/types';
import { AuthenticationError } from '@/types';

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    token: null,
    customerId: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const setError = useCallback((error: string | null) => {
    setAuthState(prev => ({ ...prev, error, isLoading: false }));
  }, []);

  const setAuthenticated = useCallback((token: string, customerId: string) => {
    setAuthState({
      token,
      customerId,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    });
  }, []);

  const setUnauthenticated = useCallback(() => {
    clearStoredToken();
    setAuthState({
      token: null,
      customerId: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  }, []);

  const validateAndStoreToken = useCallback(async (token: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

      // First, validate token structure and expiry client-side
      const customerId = validateToken(token);
      if (!customerId) {
        throw new AuthenticationError('Invalid or expired token');
      }

      // Then verify with backend if available
      try {
        const backendVerification = await verifyToken(token);
        if (!backendVerification.valid) {
          throw new AuthenticationError('Token verification failed');
        }
      } catch (error) {
        // If it's an AuthenticationError, reject the token
        if (error instanceof AuthenticationError) {
          throw error;
        }
        // If backend verification fails due to network error, continue with client-side validation
        // This allows the app to work offline or when the backend is unavailable
        console.warn('Backend token verification failed, using client-side validation:', error);
      }

      // Store token and set authenticated state
      storeToken(token);
      setAuthenticated(token, customerId);

      // Set up auto-logout timer
      const timeToExpiry = getTokenTimeToExpiry(token);
      if (timeToExpiry > 0) {
        setTimeout(() => {
          console.log('Token expired, logging out');
          logout();
        }, timeToExpiry);
      }

      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Authentication failed';
      setError(message);
      return false;
    }
  }, [setAuthenticated, setError]);

  const logout = useCallback(() => {
    setUnauthenticated();
  }, [setUnauthenticated]);

  const checkTokenExpiry = useCallback(() => {
    const token = getStoredToken();
    if (token && isTokenExpired(token)) {
      console.log('Stored token is expired, logging out');
      logout();
      return false;
    }
    return true;
  }, [logout]);

  // Initialize authentication on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // First, check if there's a token in the URL
        const urlToken = extractTokenFromUrl();
        if (urlToken) {
          await validateAndStoreToken(urlToken);
          return;
        }

        // Then check if there's a stored token
        const storedToken = getStoredToken();
        if (storedToken) {
          if (checkTokenExpiry()) {
            const customerId = validateToken(storedToken);
            if (customerId) {
              setAuthenticated(storedToken, customerId);
              return;
            }
          }
        }

        // No valid token found
        setUnauthenticated();
      } catch (error) {
        console.error('Auth initialization error:', error);
        setError('Failed to initialize authentication');
      }
    };

    initializeAuth();
  }, [validateAndStoreToken, setAuthenticated, setUnauthenticated, setError, checkTokenExpiry]);

  // Periodic token expiry check
  useEffect(() => {
    if (!authState.isAuthenticated) return;

    const interval = setInterval(() => {
      checkTokenExpiry();
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [authState.isAuthenticated, checkTokenExpiry]);

  // Development-only method to set token from dev tools
  const setTokenFromDev = import.meta.env.DEV 
    ? useCallback(async (token: string) => {
        console.log('Setting token from dev tools:', token);
        await validateAndStoreToken(token);
      }, [validateAndStoreToken])
    : undefined;

  return {
    ...authState,
    login: validateAndStoreToken,
    logout,
    checkTokenExpiry,
    setTokenFromDev,
  };
}